import React, { useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { useScreenInit } from '../useScreenInit.js';
import { NoticeComposer } from './NoticeComposer';
import { NoticeList } from './NoticeList';
import { LegacyNoticeComposer } from './LegacyNoticeComposer';
import { CommunicationsConfigurer } from './CommunicationsConfigurer';
import { useLanguage } from '../contexts/LanguageContext';
import { NOTICES } from '../data/notices';
import type { NoticeTemplate } from '../data/communications';
import type { Notice } from '../types/notice';
type SubTab = 'gerer' | 'analytiques' | 'configurer';
export function CreateNoticeForm() {
  const {
    t
  } = useLanguage();
  const screenInit = useScreenInit();
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(screenInit.activeSubTab ?? 'gerer');
  /** Within "Gérer": browsing the notice list, or inside the composer. */
  const [gererView, setGererView] = useState<'list' | 'composer'>(screenInit.gererView ?? 'list');
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  /** Notice the analytics tab is filtered on, if any. */
  const [focusedNotice, setFocusedNotice] = useState<Notice | null>(null);
  const [notices] = useState<Notice[]>(NOTICES);
  const [customTemplates, setCustomTemplates] = useState<NoticeTemplate[]>([]);
  const [contentLang, setContentLang] = useState<'fr' | 'en'>('fr');
  const [legacyMode, setLegacyMode] = useState(false);
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'gerer',
    label: t('Gérer', 'Manage')
  }, {
    id: 'analytiques',
    label: t('Analytiques', 'Analytics')
  }, {
    id: 'configurer',
    label: t('Configurer', 'Configure')
  }];
  const openComposer = (notice: Notice | null) => {
    setEditingNotice(notice);
    setGererView('composer');
  };
  const backToList = () => {
    setGererView('list');
    setEditingNotice(null);
  };
  const viewStats = (notice: Notice) => {
    setFocusedNotice(notice);
    setActiveSubTab('analytiques');
  };
  const selectTab = (id: SubTab) => {
    setActiveSubTab(id);
    // Choosing the tab directly always shows the whole-module view.
    if (id === 'analytiques') setFocusedNotice(null);
  };
  const inComposer = activeSubTab === 'gerer' && gererView === 'composer';
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1779" data-mp="1vh950bw2tx13">
      {/* Page title & navigation */}
      <div className="mb-8" data-id="element-1780" data-mp="2adqe0wgsmzkh">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-1781" data-mp="2acyhnjfdgxt5">
          <div className="min-w-0" data-id="element-1782" data-mp="25edrydxqxwiy">
            {inComposer && <button onClick={backToList} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors mb-1" data-id="element-1783" data-mp="2j73f4lp0h7hn">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1784" data-mp="2t62dnbg2bmqx" />
                {t('Retour aux avis', 'Back to notices')}
              </button>}
            <h1 className="text-2xl font-bold text-[#0F172A] font-heading truncate" data-id="element-1785" data-mp="37ryez0pf8iqs">
              {inComposer ? editingNotice ? t("Modifier l'avis", 'Edit notice') : t('Créer un avis', 'Create a notice') : t('Communications', 'Communications')}
            </h1>
          </div>

          <nav aria-label={t('Sections du module', 'Module sections')} className="flex items-center gap-2" data-id="element-1786" data-mp="32rk47awfqup2">
            {tabs.map(tab => <button key={tab.id} onClick={() => selectTab(tab.id)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeSubTab === tab.id ? 'text-white bg-[#0F172A] shadow-sm hover:bg-[#1E293B]' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1787" data-mp="2tti8navh0u96">
                {tab.label}
              </button>)}
          </nav>
        </div>

      </div>

      {activeSubTab === 'analytiques' ? <AnalyticsDashboard focusedNotice={focusedNotice} onClearFocus={() => setFocusedNotice(null)} data-id="element-1788" data-mp="1y2yecri7wrwq" /> : activeSubTab === 'configurer' ? <CommunicationsConfigurer data-id="element-1789" data-mp="1odeqf7fne7v0" /> : gererView === 'list' ? <NoticeList notices={notices} onCreate={() => openComposer(null)} onEdit={openComposer} onViewStats={viewStats} data-id="element-1790" data-mp="1karxt4b5kiwr" /> : legacyMode ? <LegacyNoticeComposer legacyMode={legacyMode} onToggleLegacy={setLegacyMode} data-id="element-1791" data-mp="1acvq6mhfc63e" /> : <NoticeComposer legacyMode={legacyMode} onToggleLegacy={setLegacyMode} contentLang={contentLang} onContentLangChange={setContentLang} editingNotice={editingNotice} customTemplates={customTemplates} onSaveTemplate={tpl => setCustomTemplates(prev => [tpl, ...prev])} onSubmit={backToList} data-id="element-1792" data-mp="15ekfk9jb1rk4" />}
    </div>;
}